import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Calendar, Clock, MapPin, Users, ChevronLeft, ChevronRight, 
  Plus, Search, Filter, Store, User, Check, X, Edit2, Trash2,
  CalendarDays, Grid3X3, List, Building2, Settings, CreditCard,
  DollarSign, Phone, Mail, Globe, AlertCircle, CheckCircle,
  Eye, EyeOff, RefreshCw, Download, Printer, ExternalLink, Send
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Switch } from './ui/switch';
import { format, addDays, startOfWeek, addWeeks, isSameDay, parseISO } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import { GreenskeeperTools } from './gps/GreenskeeperTools';
import { gpsApi } from './gps/gpsApi';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Eagle Background - Shows behind all booking content
const EagleBackground = () => (
  <div className="fixed inset-0 z-0 pointer-events-none">
    {/* Eagle Background Image */}
    <div 
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ 
        backgroundImage: 'url(/images/eagle-golf-bg.png)',
        backgroundPosition: 'center 30%'
      }}
    />
    {/* Dark overlay for content readability */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black/90" />
  </div>
);

// Eagle Header Component - Shows eagle background with date picker
const EagleOnboarding = ({ onComplete }) => {
  useEffect(() => {
    // Auto-complete immediately (no animation delay)
    const timer = setTimeout(() => onComplete?.(), 100);
    return () => clearTimeout(timer);
  }, [onComplete]);
  
  return (
    <div className="relative h-56 overflow-hidden rounded-xl mb-4 shadow-lg">
      {/* Eagle Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/images/eagle-golf-bg.png)',
          backgroundPosition: 'center 30%'
        }}
      />
      
      {/* Gradient Overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
      
      {/* Welcome Text */}
      <div className="absolute bottom-4 left-0 right-0 text-center z-10">
        <h2 className="text-white text-2xl font-bold drop-shadow-lg">Book Your Tee Time</h2>
        <p className="text-white/90 text-sm drop-shadow">Search courses • View availability • Reserve your spot</p>
      </div>
      
      {/* Par Seeker Badge */}
      <div className="absolute top-3 right-3 bg-amber-500/90 backdrop-blur-sm px-3 py-1 rounded-full z-10">
        <span className="text-white text-xs font-bold">PAR SEEKER</span>
      </div>
    </div>
  );
};

// Player Indicator Component - Clean visual for player spots
const PlayerIndicator = ({ filled, total, size = 'sm' }) => {
  const sizeClasses = size === 'sm' ? 'w-2 h-2' : 'w-3 h-3';
  const gapClass = size === 'sm' ? 'gap-1' : 'gap-1.5';
  
  return (
    <div className={`flex ${gapClass}`}>
      {[...Array(total)].map((_, i) => (
        <div 
          key={i} 
          className={`${sizeClasses} rounded-full ${
            i < filled 
              ? 'bg-[var(--ps-primary)]' 
              : 'bg-[var(--ps-border)] border border-dashed border-[var(--ps-text-muted)]'
          }`}
        />
      ))}
    </div>
  );
};

// Time Slot Component - Clean, Non-Overlapping Design
const TimeSlot = ({ time, booking, onBook, onEdit, onCancel, isProShop, courseName, isGolferView, onJoinGroup, onShare }) => {
  const isBooked = !!booking;
  const maxPlayers = 4;
  const playersBooked = booking?.players || 0;
  const spotsOpen = maxPlayers - playersBooked;
  const isFull = playersBooked >= maxPlayers;
  
  // AVAILABLE SLOT - No booking, all spots open
  if (!isBooked) {
    return (
      <button 
        onClick={() => onBook(time)}
        className="w-full border-b border-[var(--ps-border)] py-3 px-4 flex items-center gap-4 hover:bg-[var(--ps-primary)]/5 transition-all group"
        data-testid={`time-slot-${time}`}
      >
        {/* Time */}
        <div className="w-14 text-sm font-mono font-semibold text-[var(--ps-text-primary)]">
          {time}
        </div>
        
        {/* Status Pill */}
        <div className="flex-1 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Availability Badge */}
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--ps-primary)]/10 border border-[var(--ps-primary)]/20">
              <span className="w-2 h-2 rounded-full bg-[var(--ps-primary)] animate-pulse" />
              <span className="text-sm font-medium text-[var(--ps-primary)]">Available</span>
            </span>
            
            {/* Player Dots */}
            <div className="hidden sm:flex items-center gap-2 text-xs text-[var(--ps-text-muted)]">
              <PlayerIndicator filled={0} total={4} />
              <span>4 spots</span>
            </div>
          </div>
          
          {/* Book Button */}
          <span className="text-sm font-medium text-[var(--ps-primary)] group-hover:text-[var(--ps-primary-dark)] flex items-center gap-1">
            <Plus className="w-4 h-4" />
            Book
          </span>
        </div>
      </button>
    );
  }
  
  // FULL SLOT - All 4 players booked
  if (isFull) {
    return (
      <div 
        className="w-full border-b border-[var(--ps-border)] py-3 px-4 flex items-center gap-4 bg-[var(--ps-bg-elevated)]/50 opacity-60"
        data-testid={`time-slot-${time}`}
      >
        {/* Time */}
        <div className="w-14 text-sm font-mono text-[var(--ps-text-muted)] line-through">
          {time}
        </div>
        
        {/* Status */}
        <div className="flex-1 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Full Badge */}
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-500/10 border border-gray-500/20">
              <span className="text-sm font-medium text-[var(--ps-text-muted)]">Full</span>
            </span>
            
            {/* Player Info */}
            <div className="flex items-center gap-2 text-xs text-[var(--ps-text-muted)]">
              <PlayerIndicator filled={4} total={4} />
              <span>4 of 4</span>
            </div>
          </div>
          
          {/* Booked By (Pro Shop view) */}
          {isProShop && booking.user_name && (
            <span className="text-xs text-[var(--ps-text-muted)]">
              {booking.user_name}
            </span>
          )}
        </div>
      </div>
    );
  }
  
  // PARTIAL BOOKING - Some spots available (Golfer View)
  if (isGolferView && spotsOpen > 0) {
    return (
      <div 
        className="w-full border-b border-[var(--ps-border)] py-3 px-4 flex items-center gap-4 bg-amber-500/5 hover:bg-amber-500/10 transition-all"
        data-testid={`time-slot-${time}`}
      >
        {/* Time - Highlighted */}
        <div className="w-14 text-sm font-mono font-semibold text-amber-500">
          {time}
        </div>
        
        {/* Status */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              {/* Partial Badge */}
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
                <span className="text-sm font-medium text-amber-600">{playersBooked} of 4 booked</span>
              </span>
              
              {/* Open Spots Badge */}
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[var(--ps-primary)]/10 text-xs font-medium text-[var(--ps-primary)]">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ps-primary)]" />
                {spotsOpen} open
              </span>
            </div>
          </div>
          
          {/* Action Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PlayerIndicator filled={playersBooked} total={4} size="md" />
            </div>
            
            <button 
              onClick={() => onJoinGroup ? onJoinGroup(time, booking) : onBook(time)}
              className="text-sm font-medium text-[var(--ps-primary)] hover:text-[var(--ps-primary-dark)] flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Join Group
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // BOOKED SLOT - Pro Shop or Owner View
  return (
    <div 
      className="w-full border-b border-[var(--ps-border)] py-3 px-4 flex items-center gap-4 bg-[var(--ps-bg-elevated)]"
      data-testid={`time-slot-${time}`}
    >
      {/* Time */}
      <div className="w-14 text-sm font-mono font-semibold text-[var(--ps-text-primary)]">
        {time}
      </div>
      
      {/* Booking Info */}
      <div className="flex-1 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* User Avatar & Name */}
          <Avatar className="w-8 h-8 border-2 border-[var(--ps-primary)]/30">
            <AvatarImage src={booking.user_picture} />
            <AvatarFallback className="bg-[var(--ps-primary)] text-white text-xs font-bold">
              {booking.user_name?.charAt(0) || 'G'}
            </AvatarFallback>
          </Avatar>
          
          <div>
            <p className="text-sm font-medium text-[var(--ps-text-primary)]">{booking.user_name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <PlayerIndicator filled={playersBooked} total={4} />
              <span className="text-xs text-[var(--ps-text-muted)]">
                {playersBooked} of 4
              </span>
              {spotsOpen > 0 && (
                <span className="text-xs font-medium text-amber-500">
                  • {spotsOpen} open
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Share Button */}
          {onShare && booking.is_owner && (
            <button 
              onClick={() => onShare(booking)} 
              className="p-2 text-[var(--ps-text-muted)] hover:text-[var(--ps-primary)] hover:bg-[var(--ps-primary)]/10 rounded-lg transition-colors"
              data-testid={`share-booking-${booking.booking_id}`}
              title="Share & Invite"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
          
          {(isProShop || booking.is_owner) && (
            <>
              <button 
                onClick={() => onEdit(booking)} 
                className="p-2 text-[var(--ps-text-muted)] hover:text-[var(--ps-primary)] hover:bg-[var(--ps-primary)]/10 rounded-lg transition-colors"
                data-testid={`edit-booking-${booking.booking_id}`}
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button 
                onClick={() => onCancel(booking)} 
                className="p-2 text-[var(--ps-text-muted)] hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                data-testid={`cancel-booking-${booking.booking_id}`}
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Tee Sheet Grid View
const TeeSheetGrid = ({ date, bookings, timeSlots, onBook, onEdit, onCancel, isProShop, courseName, isGolferView, onJoinGroup, onShare }) => {
  return (
    <div className="bg-[var(--ps-bg-surface)] rounded-lg border border-[var(--ps-border)] overflow-hidden">
      <div className="bg-[var(--ps-bg-elevated)] px-4 py-3 border-b border-[var(--ps-border)]">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[var(--ps-text-primary)] uppercase tracking-wide text-sm">
            {format(date, 'EEEE, MMMM d, yyyy')}
          </h3>
          {courseName && (
            <span className="text-xs text-[var(--ps-text-muted)] flex items-center gap-1 bg-[var(--ps-bg-base)] px-2 py-1 rounded-full">
              <MapPin className="w-3 h-3 text-[var(--ps-primary)]" /> {courseName}
            </span>
          )}
        </div>
      </div>
      
      <div className="max-h-[450px] overflow-y-auto">
        {timeSlots.map((time) => {
          const booking = bookings.find(b => b.time === time);
          return (
            <TimeSlot
              key={time}
              time={time}
              booking={booking}
              onBook={onBook}
              onEdit={onEdit}
              onCancel={onCancel}
              isProShop={isProShop}
              courseName={courseName}
              isGolferView={isGolferView}
              onJoinGroup={onJoinGroup}
              onShare={onShare}
            />
          );
        })}
      </div>
    </div>
  );
};

// Book Tee Time Modal
const BookTeeTimeModal = ({ isOpen, onClose, selectedTime, selectedDate, courses, onSubmit, editingBooking, preSelectedCourse }) => {
  const [selectedCourse, setSelectedCourse] = useState(editingBooking?.course_id || preSelectedCourse || '');
  const [players, setPlayers] = useState(editingBooking?.players?.toString() || '1');
  const [notes, setNotes] = useState(editingBooking?.notes || '');
  const [time, setTime] = useState(editingBooking?.time || selectedTime || '08:00');
  const [submitting, setSubmitting] = useState(false);
  
  // Filter to only show courses that accept online booking
  const bookableCourses = courses.filter(c => c.proshop_managed);
  const selectedCourseData = courses.find(c => c.course_id === selectedCourse);
  
  // Update selected course when preSelectedCourse changes
  useEffect(() => {
    if (preSelectedCourse && !selectedCourse) {
      setSelectedCourse(preSelectedCourse);
    }
  }, [preSelectedCourse, selectedCourse]);
  
  const handleSubmit = async () => {
    if (!selectedCourse) {
      toast.error('Please select a Par Seeker partner course');
      return;
    }
    
    // Check if selected course accepts online booking
    const course = courses.find(c => c.course_id === selectedCourse);
    if (!course?.proshop_managed) {
      toast.error('This course does not accept online bookings yet');
      return;
    }
    
    setSubmitting(true);
    try {
      await onSubmit({
        course_id: selectedCourse,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time,
        players: parseInt(players),
        notes
      });
      onClose();
    } catch (error) {
      toast.error('Failed to book tee time');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-sm" data-testid="book-tee-time-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[var(--ps-primary)]" />
            {editingBooking ? 'Edit Booking' : 'Book Tee Time'}
          </DialogTitle>
          <DialogDescription className="text-[var(--ps-text-muted)]">
            {editingBooking ? 'Update your tee time details below.' : 'Book at a Par Seeker partner course.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Course Selection - Only Par Seeker Partners */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Course
              <span className="text-[10px] px-1.5 py-0.5 bg-[var(--ps-primary)]/20 text-[var(--ps-primary)] rounded ml-1">
                PAR SEEKER PARTNERS
              </span>
            </label>
            {bookableCourses.length === 0 ? (
              <div className="bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg p-3 text-center">
                <p className="text-sm text-[var(--ps-text-muted)]">No courses available for online booking yet</p>
                <p className="text-xs text-[var(--ps-text-muted)] mt-1">Check back soon as more courses join Par Seeker!</p>
              </div>
            ) : (
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="w-full bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-[var(--ps-text-primary)]"
              >
                <option value="">Select a course...</option>
                {bookableCourses.map(course => (
                  <option key={course.course_id} value={course.course_id}>
                    ✓ {course.name} {course.green_fee ? `• $${course.green_fee}` : ''}
                  </option>
                ))}
              </select>
            )}
            
            {/* Show selected course details */}
            {selectedCourseData && (
              <div className="mt-2 p-2 bg-[var(--ps-primary)]/10 rounded-lg border border-[var(--ps-primary)]/30">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[var(--ps-primary)]" />
                  <span className="text-sm font-medium text-[var(--ps-text-primary)]">{selectedCourseData.name}</span>
                </div>
                <p className="text-xs text-[var(--ps-text-muted)] ml-6">
                  {selectedCourseData.holes || 18} holes • Par {selectedCourseData.par || 72}
                  {selectedCourseData.payment_options?.pay_at_course && ' • Pay at course available'}
                </p>
              </div>
            )}
          </div>
          
          {/* Date Display */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">Date</label>
            <div className="bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-[var(--ps-text-primary)]">
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </div>
          </div>
          
          {/* Time Selection */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">Time</label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
            />
          </div>
          
          {/* Players */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">Number of Players</label>
            <select
              value={players}
              onChange={(e) => setPlayers(e.target.value)}
              className="w-full bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-[var(--ps-text-primary)]"
            >
              {[1, 2, 3, 4].map(n => (
                <option key={n} value={n}>{n} {n === 1 ? 'Player' : 'Players'}</option>
              ))}
            </select>
          </div>
          
          {/* Notes */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">Notes (optional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Special requests, cart preferences..."
              className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
            />
          </div>
          
          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedCourse || bookableCourses.length === 0}
            className="w-full bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]"
          >
            {submitting ? 'Booking...' : editingBooking ? 'Update Booking' : 'Book Tee Time'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Share & Invite Modal for Tee Time Bookings
const ShareBookingModal = ({ isOpen, onClose, booking, courseName, onAddToEvents }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [addingToEvents, setAddingToEvents] = useState(false);
  const [addedToEvents, setAddedToEvents] = useState(false);
  
  const formatDateTime = () => {
    if (!booking) return '';
    const dateStr = format(parseISO(booking.date), 'EEEE, MMMM d, yyyy');
    return `${dateStr} at ${booking.time}`;
  };
  
  const handleAddToEvents = async () => {
    setAddingToEvents(true);
    try {
      await onAddToEvents(booking);
      setAddedToEvents(true);
      toast.success('Added to your events calendar!');
    } catch (error) {
      toast.error('Failed to add to events');
    } finally {
      setAddingToEvents(false);
    }
  };
  
  const handleShareLink = () => {
    const shareText = `Join me for golf at ${courseName || booking?.course_name}!\n📅 ${formatDateTime()}\n⛳ ${booking?.players || 1} player(s) booked, spots available!\n\nBook your tee time on Par Seeker!`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Par Seeker Tee Time',
        text: shareText,
        url: window.location.origin + '/book'
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Share text copied to clipboard!');
    }
  };
  
  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setInviting(true);
    try {
      // This would send an invitation email via the backend
      await axios.post(`${API}/tee-times/${booking.booking_id}/invite`, {
        email: inviteEmail.trim()
      });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };
  
  if (!booking) return null;
  
  const spotsOpen = 4 - (booking.players || 1);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-sm" data-testid="share-booking-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[var(--ps-primary)]" />
            Share & Invite
          </DialogTitle>
          <DialogDescription className="text-[var(--ps-text-muted)]">
            Add to your events or invite players to join your tee time.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Booking Summary */}
          <div className="bg-[var(--ps-bg-elevated)] rounded-lg p-3 border border-[var(--ps-border)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[var(--ps-primary)]/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[var(--ps-primary)]" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm text-[var(--ps-text-primary)]">
                  {courseName || booking.course_name}
                </p>
                <p className="text-xs text-[var(--ps-text-muted)]">
                  {formatDateTime()}
                </p>
              </div>
            </div>
            
            <div className="mt-3 pt-3 border-t border-[var(--ps-border)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PlayerIndicator filled={booking.players || 1} total={4} size="md" />
                <span className="text-xs text-[var(--ps-text-muted)]">
                  {booking.players || 1} of 4 booked
                </span>
              </div>
              {spotsOpen > 0 && (
                <span className="text-xs font-medium text-[var(--ps-primary)] bg-[var(--ps-primary)]/10 px-2 py-1 rounded-full">
                  {spotsOpen} spot{spotsOpen > 1 ? 's' : ''} open
                </span>
              )}
            </div>
          </div>
          
          {/* Add to Events */}
          <div>
            <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-2 block">
              Add to Your Calendar
            </label>
            <Button
              onClick={handleAddToEvents}
              disabled={addingToEvents || addedToEvents}
              className={`w-full ${addedToEvents ? 'bg-green-600 hover:bg-green-600' : 'bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)]'}`}
            >
              {addedToEvents ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Added to Events
                </>
              ) : addingToEvents ? (
                'Adding...'
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Add to My Events
                </>
              )}
            </Button>
            <p className="text-xs text-[var(--ps-text-muted)] mt-1.5">
              Creates an event you can share and invite buddies to join.
            </p>
          </div>
          
          {/* Share Link */}
          <div>
            <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-2 block">
              Share Tee Time
            </label>
            <Button
              onClick={handleShareLink}
              variant="outline"
              className="w-full border-[var(--ps-border)] text-[var(--ps-text-primary)]"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Share Link
            </Button>
          </div>
          
          {/* Invite by Email */}
          {spotsOpen > 0 && (
            <div>
              <label className="text-sm font-medium text-[var(--ps-text-secondary)] mb-2 block">
                Invite Player by Email
              </label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="buddy@email.com"
                  className="flex-1 bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                />
                <Button
                  onClick={handleInvite}
                  disabled={inviting || !inviteEmail.trim()}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  {inviting ? '...' : 'Invite'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Course Setup Modal for Pro Shop
const CourseSetupModal = ({ isOpen, onClose, existingCourse, onSave }) => {
  const [courseName, setCourseName] = useState(existingCourse?.name || '');
  const [courseAddress, setCourseAddress] = useState(existingCourse?.address || '');
  const [coursePhone, setCoursePhone] = useState(existingCourse?.phone || '');
  const [courseEmail, setCourseEmail] = useState(existingCourse?.email || '');
  const [courseWebsite, setCourseWebsite] = useState(existingCourse?.website || '');
  const [holes, setHoles] = useState(existingCourse?.holes || '18');
  const [par, setPar] = useState(existingCourse?.par || '72');
  
  // Payment options
  const [acceptPrepay, setAcceptPrepay] = useState(existingCourse?.payment_options?.prepay ?? true);
  const [acceptPayAtCourse, setAcceptPayAtCourse] = useState(existingCourse?.payment_options?.pay_at_course ?? true);
  const [acceptMembersOnly, setAcceptMembersOnly] = useState(existingCourse?.payment_options?.members_only ?? false);
  const [greenFee, setGreenFee] = useState(existingCourse?.green_fee || '');
  const [cartFee, setCartFee] = useState(existingCourse?.cart_fee || '');
  
  // Booking settings
  const [bookingWindow, setBookingWindow] = useState(existingCourse?.booking_window || '7'); // days in advance
  const [minPlayers, setMinPlayers] = useState(existingCourse?.min_players || '1');
  const [maxPlayers, setMaxPlayers] = useState(existingCourse?.max_players || '4');
  const [teeTimeInterval, setTeeTimeInterval] = useState(existingCourse?.tee_time_interval || '10'); // minutes
  const [firstTeeTime, setFirstTeeTime] = useState(existingCourse?.first_tee_time || '06:00');
  const [lastTeeTime, setLastTeeTime] = useState(existingCourse?.last_tee_time || '18:00');
  const [requirePhone, setRequirePhone] = useState(existingCourse?.require_phone ?? false);
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !existingCourse) return;
    const settings = existingCourse.booking_settings || {};
    setCourseName(existingCourse.name || ''); setCourseAddress(existingCourse.address || '');
    setCoursePhone(existingCourse.phone || ''); setCourseEmail(existingCourse.email || ''); setCourseWebsite(existingCourse.website || '');
    setHoles(existingCourse.holes || 18); setPar(existingCourse.par || 72);
    setGreenFee(existingCourse.green_fee ?? ''); setCartFee(existingCourse.cart_fee ?? '');
    setAcceptPrepay(existingCourse.payment_options?.prepay ?? false); setAcceptPayAtCourse(existingCourse.payment_options?.pay_at_course ?? true);
    setAcceptMembersOnly(existingCourse.payment_options?.members_only ?? false);
    setBookingWindow(settings.booking_window ?? 7); setMinPlayers(settings.min_players ?? 1); setMaxPlayers(settings.max_players ?? 4);
    setTeeTimeInterval(settings.tee_time_interval ?? 10); setFirstTeeTime(settings.first_tee_time || '06:00'); setLastTeeTime(settings.last_tee_time || '18:00'); setRequirePhone(settings.require_phone ?? false);
  }, [isOpen, existingCourse]);

  const handleSave = async () => {
    if (!courseName.trim()) {
      toast.error('Course name is required');
      return;
    }
    
    setSaving(true);
    try {
      const courseData = {
        name: courseName,
        address: courseAddress,
        phone: coursePhone,
        email: courseEmail,
        website: courseWebsite,
        holes: parseInt(holes),
        par: parseInt(par),
        green_fee: greenFee ? parseFloat(greenFee) : null,
        cart_fee: cartFee ? parseFloat(cartFee) : null,
        payment_options: {
          prepay: acceptPrepay,
          pay_at_course: acceptPayAtCourse,
          members_only: acceptMembersOnly
        },
        booking_settings: {
          booking_window: parseInt(bookingWindow),
          min_players: parseInt(minPlayers),
          max_players: parseInt(maxPlayers),
          tee_time_interval: parseInt(teeTimeInterval),
          first_tee_time: firstTeeTime,
          last_tee_time: lastTeeTime,
          require_phone: requirePhone
        }
      };
      
      await onSave(courseData);
      onClose();
    } catch (error) {
      toast.error('Failed to save course settings');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="course-setup-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-500" />
            {existingCourse ? 'Edit Course Settings' : 'Register Your Course'}
          </DialogTitle>
          <DialogDescription className="text-[var(--ps-text-muted)]">
            Set up your course details and booking preferences
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Course Info Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-amber-500 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Course Information
            </h4>
            
            <div className="grid gap-3">
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Course Name *</label>
                <Input
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="e.g., Pebble Beach Golf Links"
                  className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                  data-testid="course-name-input"
                />
              </div>
              
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Address</label>
                <Input
                  value={courseAddress}
                  data-testid="course-address-input"
                  onChange={(e) => setCourseAddress(e.target.value)}
                  placeholder="1700 17 Mile Dr, Pebble Beach, CA"
                  className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Holes</label>
                  <select
                    value={holes}
                    data-testid="course-holes-select"
                    onChange={(e) => setHoles(e.target.value)}
                    className="w-full bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-sm text-[var(--ps-text-primary)]"
                  >
                    <option value="9">9 Holes</option>
                    <option value="18">18 Holes</option>
                    <option value="27">27 Holes</option>
                    <option value="36">36 Holes</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Par</label>
                  <Input
                    type="number"
                    value={par}
                    data-testid="course-par-input"
                    onChange={(e) => setPar(e.target.value)}
                    placeholder="72"
                    className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Phone
                  </label>
                  <Input
                    value={coursePhone}
                    data-testid="course-phone-input"
                    onChange={(e) => setCoursePhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                  />
                </div>
                <div>
                  <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email
                  </label>
                  <Input
                    value={courseEmail}
                    data-testid="course-email-input"
                    onChange={(e) => setCourseEmail(e.target.value)}
                    placeholder="proshop@course.com"
                    className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block flex items-center gap-1">
                  <Globe className="w-3 h-3" /> Website
                </label>
                <Input
                  value={courseWebsite}
                  data-testid="course-website-input"
                  onChange={(e) => setCourseWebsite(e.target.value)}
                  placeholder="https://www.yourcourse.com"
                  className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                />
              </div>
            </div>
          </div>
          
          {/* Payment Options Section */}
          <div className="space-y-3 pt-3 border-t border-[var(--ps-border)]">
            <h4 className="text-sm font-semibold text-green-500 flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Payment Options
            </h4>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Green Fee ($)</label>
                <Input
                  type="number"
                  value={greenFee}
                  onChange={(e) => setGreenFee(e.target.value)}
                  placeholder="75.00"
                  className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                  data-testid="green-fee-input"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Cart Fee ($)</label>
                <Input
                  type="number"
                  value={cartFee}
                  data-testid="course-cart-fee-input"
                  onChange={(e) => setCartFee(e.target.value)}
                  placeholder="25.00"
                  className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                />
              </div>
            </div>
            
            <div className="space-y-2 bg-[var(--ps-bg-base)] rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-[var(--ps-text-primary)]">Accept Prepay Online</span>
                </div>
                <Switch 
                  checked={acceptPrepay} 
                  onCheckedChange={setAcceptPrepay}
                  data-testid="accept-prepay-toggle"
                />
              </div>
              <p className="text-xs text-[var(--ps-text-muted)] pl-6">Golfers pay when booking online</p>
            </div>
            
            <div className="space-y-2 bg-[var(--ps-bg-base)] rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Store className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-[var(--ps-text-primary)]">Pay at Course</span>
                </div>
                <Switch 
                  checked={acceptPayAtCourse} 
                  onCheckedChange={setAcceptPayAtCourse}
                  data-testid="pay-at-course-toggle"
                />
              </div>
              <p className="text-xs text-[var(--ps-text-muted)] pl-6">Golfers pay when they arrive</p>
            </div>
            
            <div className="space-y-2 bg-[var(--ps-bg-base)] rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-[var(--ps-text-primary)]">Members Only</span>
                </div>
                <Switch 
                  checked={acceptMembersOnly} 
                  onCheckedChange={setAcceptMembersOnly}
                  data-testid="members-only-toggle"
                />
              </div>
              <p className="text-xs text-[var(--ps-text-muted)] pl-6">Restrict bookings to club members</p>
            </div>
          </div>
          
          {/* Booking Settings Section */}
          <div className="space-y-3 pt-3 border-t border-[var(--ps-border)]">
            <h4 className="text-sm font-semibold text-blue-500 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Booking Settings
            </h4>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Booking Window (days)</label>
                <select
                  value={bookingWindow}
                  data-testid="course-booking-window-select"
                  onChange={(e) => setBookingWindow(e.target.value)}
                  className="w-full bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-sm text-[var(--ps-text-primary)]"
                >
                  <option value="1">1 day ahead</option>
                  <option value="3">3 days ahead</option>
                  <option value="7">7 days ahead</option>
                  <option value="14">14 days ahead</option>
                  <option value="30">30 days ahead</option>
                  <option value="60">60 days ahead</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Tee Time Interval</label>
                <select
                  value={teeTimeInterval}
                  data-testid="course-tee-interval-select"
                  onChange={(e) => setTeeTimeInterval(e.target.value)}
                  className="w-full bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-sm text-[var(--ps-text-primary)]"
                >
                  <option value="7">7 minutes</option>
                  <option value="8">8 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="12">12 minutes</option>
                  <option value="15">15 minutes</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">First Tee Time</label>
                <Input
                  type="time"
                  value={firstTeeTime}
                  data-testid="course-first-tee-input"
                  onChange={(e) => setFirstTeeTime(e.target.value)}
                  className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Last Tee Time</label>
                <Input
                  type="time"
                  value={lastTeeTime}
                  data-testid="course-last-tee-input"
                  onChange={(e) => setLastTeeTime(e.target.value)}
                  className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Min Players</label>
                <select
                  value={minPlayers}
                  data-testid="course-min-players-select"
                  onChange={(e) => setMinPlayers(e.target.value)}
                  className="w-full bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-sm text-[var(--ps-text-primary)]"
                >
                  {[1, 2, 3, 4].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--ps-text-secondary)] mb-1 block">Max Players</label>
                <select
                  value={maxPlayers}
                  data-testid="course-max-players-select"
                  onChange={(e) => setMaxPlayers(e.target.value)}
                  className="w-full bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-sm text-[var(--ps-text-primary)]"
                >
                  {[2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="space-y-2 bg-[var(--ps-bg-base)] rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-[var(--ps-text-primary)]">Require Phone Number</span>
                </div>
                <Switch 
                  checked={requirePhone} 
                  data-testid="course-require-phone-switch"
                  onCheckedChange={setRequirePhone}
                />
              </div>
              <p className="text-xs text-[var(--ps-text-muted)] pl-6">Require phone for booking confirmation</p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 pt-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[var(--ps-border)]"
              data-testid="course-settings-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="save-course-btn"
            >
              {saving ? 'Saving...' : 'Save Course'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Pro Shop Booking Modal - For golf courses to add walk-ins and phone reservations
const ProShopBookingModal = ({ isOpen, onClose, course, selectedDate, editingBooking, onSuccess }) => {
  const [playerName, setPlayerName] = useState(editingBooking?.guest_name || '');
  const [playerPhone, setPlayerPhone] = useState(editingBooking?.guest_phone || '');
  const [playerEmail, setPlayerEmail] = useState(editingBooking?.guest_email || '');
  const [selectedTime, setSelectedTime] = useState(editingBooking?.time || '08:00');
  const [players, setPlayers] = useState(editingBooking?.players || 1);
  const [notes, setNotes] = useState(editingBooking?.notes || '');
  const [paymentStatus, setPaymentStatus] = useState(editingBooking?.payment_status || 'pending');
  const [saving, setSaving] = useState(false);
  
  // Generate time slots
  const timeSlots = [];
  for (let hour = 6; hour <= 18; hour++) {
    for (let min = 0; min < 60; min += 10) {
      if (hour === 18 && min > 0) break;
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      timeSlots.push(timeStr);
    }
  }
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setPlayerName(editingBooking?.guest_name || '');
      setPlayerPhone(editingBooking?.guest_phone || '');
      setPlayerEmail(editingBooking?.guest_email || '');
      setSelectedTime(editingBooking?.time || '08:00');
      setPlayers(editingBooking?.players || 1);
      setNotes(editingBooking?.notes || '');
      setPaymentStatus(editingBooking?.payment_status || 'pending');
    }
  }, [isOpen, editingBooking]);
  
  const handleSubmit = async () => {
    if (!playerName.trim()) {
      toast.error('Player name is required');
      return;
    }
    
    if (!course?.course_id) {
      toast.error('Please set up your course first');
      return;
    }
    
    setSaving(true);
    try {
      const bookingData = {
        course_id: course.course_id,
        course_name: course.name,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        players: players,
        notes: notes,
        guest_name: playerName,
        guest_phone: playerPhone,
        guest_email: playerEmail,
        payment_status: paymentStatus,
        booked_by_proshop: true,
        status: 'confirmed' // Pro shop bookings are auto-confirmed
      };
      
      if (editingBooking) {
        await axios.put(`${API}/tee-times/${editingBooking.booking_id}`, bookingData);
        toast.success('Booking updated!');
      } else {
        await axios.post(`${API}/tee-times/proshop-booking`, bookingData);
        toast.success('Booking added!');
      }
      
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save booking');
    } finally {
      setSaving(false);
    }
  };
  
  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-sm" data-testid="proshop-booking-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-500" />
            {editingBooking ? 'Edit Booking' : 'Add Booking'}
          </DialogTitle>
          <DialogDescription className="text-[var(--ps-text-muted)]">
            Add a walk-in or phone reservation for {course?.name || 'your course'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Player Name - Required */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">
              Player Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="John Smith"
              className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
              data-testid="proshop-player-name"
            />
          </div>
          
          {/* Player Phone */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block flex items-center gap-1">
              <Phone className="w-3 h-3" /> Phone (optional)
            </label>
            <Input
              value={playerPhone}
              onChange={(e) => setPlayerPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
            />
          </div>
          
          {/* Date - Read only, shows selected date */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">Date</label>
            <div className="bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-[var(--ps-text-primary)]">
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </div>
          </div>
          
          {/* Time Selection */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">Time</label>
            <select
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="w-full bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-[var(--ps-text-primary)]"
              data-testid="proshop-time-select"
            >
              {timeSlots.map(time => (
                <option key={time} value={time}>{formatTime(time)}</option>
              ))}
            </select>
          </div>
          
          {/* Number of Players */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">Number of Players</label>
            <select
              value={players}
              onChange={(e) => setPlayers(parseInt(e.target.value))}
              className="w-full bg-[var(--ps-bg-base)] border border-[var(--ps-border)] rounded-lg px-3 py-2 text-[var(--ps-text-primary)]"
            >
              {[1, 2, 3, 4].map(n => (
                <option key={n} value={n}>{n} Player{n > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
          
          {/* Payment Status */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">Payment Status</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'pending', label: 'Pay at Course', color: 'amber' },
                { value: 'paid', label: 'Paid', color: 'green' },
                { value: 'unpaid', label: 'Unpaid', color: 'red' }
              ].map(status => (
                <button
                  key={status.value}
                  onClick={() => setPaymentStatus(status.value)}
                  className={`px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    paymentStatus === status.value
                      ? status.color === 'green' ? 'bg-green-500/20 border-green-500/50 text-green-500'
                        : status.color === 'amber' ? 'bg-amber-500/20 border-amber-500/50 text-amber-500'
                        : 'bg-red-500/20 border-red-500/50 text-red-500'
                      : 'bg-[var(--ps-bg-base)] border-[var(--ps-border)] text-[var(--ps-text-secondary)]'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Notes */}
          <div>
            <label className="text-sm text-[var(--ps-text-secondary)] mb-1 block">Notes (optional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Cart requested, special needs..."
              className="bg-[var(--ps-bg-base)] border-[var(--ps-border)]"
            />
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[var(--ps-border)]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="proshop-save-booking-btn"
            >
              {saving ? 'Saving...' : editingBooking ? 'Update' : 'Add Booking'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Booking Management Row Component
const BookingManagementRow = ({ booking, onConfirm, onCancel, onEdit, onNoShow }) => {
  const statusColors = {
    confirmed: 'bg-green-500/20 text-green-500 border-green-500/30',
    pending: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
    cancelled: 'bg-red-500/20 text-red-500 border-red-500/30',
    completed: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
    no_show: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };
  
  const paymentStatusColors = {
    paid: 'text-green-500',
    pending: 'text-amber-500',
    unpaid: 'text-red-500'
  };
  
  return (
    <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-lg p-3 space-y-2" data-testid={`booking-row-${booking.booking_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-lg font-mono font-bold text-[var(--ps-primary)]">
            {booking.time}
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColors[booking.status] || statusColors.pending}`}>
            {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || 'Pending'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-[var(--ps-text-muted)]">{booking.players} players</span>
          <span className={`text-xs ${paymentStatusColors[booking.payment_status] || paymentStatusColors.pending}`}>
            • {booking.payment_status === 'paid' ? 'Paid' : booking.payment_method === 'prepay' ? 'Prepaid' : 'Pay at Course'}
          </span>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Avatar className="w-8 h-8">
          <AvatarImage src={booking.user_picture} />
          <AvatarFallback className="bg-[var(--ps-primary)] text-white text-xs">
            {booking.user_name?.charAt(0) || 'G'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--ps-text-primary)] truncate">{booking.user_name}</p>
          {booking.user_phone && (
            <p className="text-xs text-[var(--ps-text-muted)]">{booking.user_phone}</p>
          )}
        </div>
      </div>
      
      {booking.notes && (
        <p className="text-xs text-[var(--ps-text-muted)] bg-[var(--ps-bg-base)] rounded p-2">
          Note: {booking.notes}
        </p>
      )}
      
      <div className="flex gap-1 pt-1">
        {booking.status === 'pending' && (
          <Button
            size="sm"
            onClick={() => onConfirm(booking)}
            className="flex-1 h-7 text-xs bg-green-500 hover:bg-green-600"
            data-testid={`confirm-booking-${booking.booking_id}`}
          >
            <Check className="w-3 h-3 mr-1" /> Confirm
          </Button>
        )}
        {booking.status !== 'cancelled' && booking.status !== 'no_show' && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(booking)}
              className="h-7 text-xs border-[var(--ps-border)]"
            >
              <Edit2 className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onNoShow(booking)}
              className="h-7 text-xs border-[var(--ps-border)] text-gray-400"
            >
              <EyeOff className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onCancel(booking)}
              className="h-7 text-xs border-red-500/30 text-red-500 hover:bg-red-500/10"
              data-testid={`cancel-booking-proshop-${booking.booking_id}`}
            >
              <X className="w-3 h-3" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// Pro Shop Management View - Enhanced
const ProShopView = ({ 
  courses, 
  bookings, 
  selectedCourse, 
  setSelectedCourse, 
  selectedDate,
  onRefresh,
  user,
  onEditBooking,
  onCancelBooking
}) => {
  const [activeTab, setActiveTab] = useState('schedule'); // schedule, manage, settings
  const [showCourseSetup, setShowCourseSetup] = useState(false);
  const [showProShopBooking, setShowProShopBooking] = useState(false);
  const [editingProShopBooking, setEditingProShopBooking] = useState(null);
  const [myCourse, setMyCourse] = useState(null);
  const [loadingCourse, setLoadingCourse] = useState(true);
  const [bookingFilter, setBookingFilter] = useState('all'); // all, pending, confirmed
  
  // Fetch pro shop's course
  useEffect(() => {
    const fetchMyCourse = async () => {
      setLoadingCourse(true);
      try {
        const response = await axios.get(`${API}/proshop/my-course`);
        setMyCourse(response.data);
        if (response.data?.course_id) {
          setSelectedCourse(response.data.course_id);
        }
      } catch (error) {
        // No course set up yet
        setMyCourse(null);
      } finally {
        setLoadingCourse(false);
      }
    };
    fetchMyCourse();
  }, [setSelectedCourse]);
  
  const handleSaveCourse = async (courseData) => {
    try {
      if (myCourse) {
        // Update existing
        const response = await axios.put(`${API}/proshop/my-course`, courseData);
        setMyCourse(response.data);
        toast.success('Course settings updated!');
      } else {
        // Create new
        const response = await axios.post(`${API}/proshop/register-course`, courseData);
        setMyCourse(response.data);
        setSelectedCourse(response.data.course_id);
        toast.success('Course registered successfully!');
      }
    } catch (error) {
      throw error;
    }
  };
  
  const handleConfirmBooking = async (booking) => {
    try {
      await axios.put(`${API}/tee-times/${booking.booking_id}`, { status: 'confirmed' });
      toast.success('Booking confirmed!');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to confirm booking');
    }
  };
  
  const handleNoShow = async (booking) => {
    if (!window.confirm('Mark this booking as No-Show?')) return;
    try {
      await axios.put(`${API}/tee-times/${booking.booking_id}`, { status: 'no_show' });
      toast.success('Marked as no-show');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };
  
  // Filter bookings
  const filteredBookings = bookings.filter(b => {
    if (bookingFilter === 'pending') return b.status === 'pending';
    if (bookingFilter === 'confirmed') return b.status === 'confirmed';
    return b.status !== 'cancelled';
  }).sort((a, b) => a.time.localeCompare(b.time));
  
  // Stats
  const todayStats = {
    total: bookings.filter(b => b.status !== 'cancelled').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    pending: bookings.filter(b => b.status === 'pending').length,
    players: bookings.filter(b => b.status !== 'cancelled').reduce((sum, b) => sum + (b.players || 1), 0),
    revenue: bookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + (b.amount_paid || 0), 0)
  };
  
  return (
    <div className="space-y-4">
      {/* Pro Shop Header */}
      <div className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-500/10 to-green-500/10 rounded-lg border border-amber-500/30">
        <div className="flex items-center gap-2">
          <Store className="w-5 h-5 text-amber-500" />
          <div>
            <h3 className="font-semibold text-[var(--ps-text-primary)]">
              {myCourse?.name || 'Pro Shop Dashboard'}
            </h3>
            <p className="text-xs text-[var(--ps-text-muted)]">
              {myCourse ? `${myCourse.holes || 18} holes • Par ${myCourse.par || 72}` : 'Set up your course to get started'}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowCourseSetup(true)}
          className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
          data-testid="course-settings-btn"
        >
          <Settings className="w-4 h-4 mr-1" />
          {myCourse ? 'Settings' : 'Setup'}
        </Button>
      </div>
      
      {/* Tab Navigation */}
      <div className="flex bg-[var(--ps-bg-elevated)] rounded-lg p-1">
        {[
          { id: 'schedule', label: 'Today\'s Schedule', icon: Calendar },
          { id: 'manage', label: 'Manage Bookings', icon: List },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-amber-500 text-white'
                : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
            }`}
            data-testid={`proshop-tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-[var(--ps-bg-surface)] rounded-lg p-2 text-center border border-[var(--ps-border)]">
          <p className="text-xl font-bold text-[var(--ps-primary)]">{todayStats.total}</p>
          <p className="text-[10px] text-[var(--ps-text-muted)]">Bookings</p>
        </div>
        <div className="bg-[var(--ps-bg-surface)] rounded-lg p-2 text-center border border-[var(--ps-border)]">
          <p className="text-xl font-bold text-green-500">{todayStats.confirmed}</p>
          <p className="text-[10px] text-[var(--ps-text-muted)]">Confirmed</p>
        </div>
        <div className="bg-[var(--ps-bg-surface)] rounded-lg p-2 text-center border border-[var(--ps-border)]">
          <p className="text-xl font-bold text-amber-500">{todayStats.pending}</p>
          <p className="text-[10px] text-[var(--ps-text-muted)]">Pending</p>
        </div>
        <div className="bg-[var(--ps-bg-surface)] rounded-lg p-2 text-center border border-[var(--ps-border)]">
          <p className="text-xl font-bold text-blue-500">{todayStats.players}</p>
          <p className="text-[10px] text-[var(--ps-text-muted)]">Players</p>
        </div>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'schedule' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[var(--ps-text-primary)]">
              {format(selectedDate, 'EEEE, MMMM d')}
            </h4>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={onRefresh}
                className="h-7 border-[var(--ps-border)]"
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
            </div>
          </div>
          
          {filteredBookings.length === 0 ? (
            <div className="text-center py-8 text-[var(--ps-text-muted)]">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No bookings for this date</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredBookings.map(booking => (
                <BookingManagementRow
                  key={booking.booking_id}
                  booking={booking}
                  onConfirm={handleConfirmBooking}
                  onCancel={onCancelBooking}
                  onEdit={onEditBooking}
                  onNoShow={handleNoShow}
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'manage' && (
        <div className="space-y-3">
          {/* Filter Pills */}
          <div className="flex gap-2">
            {[
              { id: 'all', label: 'All' },
              { id: 'pending', label: 'Pending', count: todayStats.pending },
              { id: 'confirmed', label: 'Confirmed', count: todayStats.confirmed },
            ].map(filter => (
              <button
                key={filter.id}
                onClick={() => setBookingFilter(filter.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  bookingFilter === filter.id
                    ? 'bg-amber-500 text-white'
                    : 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-secondary)] border border-[var(--ps-border)]'
                }`}
              >
                {filter.label}
                {filter.count !== undefined && ` (${filter.count})`}
              </button>
            ))}
          </div>
          
          {/* Bookings List */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredBookings.map(booking => (
              <BookingManagementRow
                key={booking.booking_id}
                booking={booking}
                onConfirm={handleConfirmBooking}
                onCancel={onCancelBooking}
                onEdit={onEditBooking}
                onNoShow={handleNoShow}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-2 pt-2">
        <Button
          variant="outline"
          className="border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
          onClick={() => {/* TODO: Block time slots */}}
        >
          <X className="w-4 h-4 mr-1" />
          Block Times
        </Button>
        <Button
          variant="outline"
          className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
          onClick={() => setShowProShopBooking(true)}
          data-testid="add-walkin-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Walk-in
        </Button>
      </div>
      
      {/* Course Setup Modal */}
      <CourseSetupModal
        isOpen={showCourseSetup}
        onClose={() => setShowCourseSetup(false)}
        existingCourse={myCourse}
        onSave={handleSaveCourse}
      />
      
      {/* Pro Shop Booking Modal */}
      <ProShopBookingModal
        isOpen={showProShopBooking}
        onClose={() => {
          setShowProShopBooking(false);
          setEditingProShopBooking(null);
        }}
        course={myCourse}
        selectedDate={selectedDate}
        editingBooking={editingProShopBooking}
        onSuccess={() => {
          setShowProShopBooking(false);
          setEditingProShopBooking(null);
          onRefresh?.();
        }}
      />
    </div>
  );
};

// Course Discovery Component - Enhanced search with Par Seeker Partners
const CourseDiscovery = ({ courses, selectedCourse, onSelectCourse, searchQuery, onSearchChange, selectedDate }) => {
  const [showResults, setShowResults] = useState(false);
  const [webSearchResults, setWebSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('partners'); // partners, all, search
  
  // Filter courses that offer online booking through Par Seeker
  const partnerCourses = courses.filter(c => c.proshop_managed);
  const allCourses = courses;
  
  // Filter by search query
  const filteredPartners = searchQuery 
    ? partnerCourses.filter(c => c.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : partnerCourses;
    
  const filteredAll = searchQuery
    ? allCourses.filter(c => c.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : allCourses;
  
  // Get selected course details
  const selectedCourseData = courses.find(c => c.course_id === selectedCourse);
  
  // Handle web search for golf courses
  const handleWebSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/courses/search?q=${encodeURIComponent(searchQuery)}`);
      setWebSearchResults(response.data?.results || []);
      setActiveTab('search');
    } catch (error) {
      console.error('Web search failed:', error);
      // Show mock results for demo
      setWebSearchResults([
        { name: `${searchQuery} Golf Club`, location: 'Search results', is_external: true },
        { name: `${searchQuery} Country Club`, location: 'Nearby', is_external: true },
      ]);
      setActiveTab('search');
    } finally {
      setIsSearching(false);
    }
  };
  
  return (
    <div className="space-y-3">
      {/* Selected Course Display */}
      {selectedCourseData && (
        <div className="bg-gradient-to-r from-[var(--ps-primary)]/10 to-green-500/10 rounded-lg p-3 border border-[var(--ps-primary)]/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[var(--ps-primary)]" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[var(--ps-text-primary)]">{selectedCourseData.name}</span>
                  {selectedCourseData.proshop_managed && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-[var(--ps-primary)] text-white rounded-full font-medium">
                      PAR SEEKER
                    </span>
                  )}
                </div>
                <p className="text-xs text-[var(--ps-text-muted)]">
                  {selectedCourseData.holes || 18} holes • Par {selectedCourseData.par || 72}
                  {selectedCourseData.green_fee && ` • $${selectedCourseData.green_fee}`}
                </p>
              </div>
            </div>
            <button 
              onClick={() => onSelectCourse('')}
              className="text-xs text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]"
            >
              Change
            </button>
          </div>
        </div>
      )}
      
      {/* Search Input */}
      {!selectedCourse && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ps-text-muted)]" />
            <Input
              value={searchQuery}
              onChange={(e) => {
                onSearchChange(e.target.value);
                setShowResults(true);
              }}
              onFocus={() => setShowResults(true)}
              placeholder="Search golf courses..."
              className="pl-9 pr-20 bg-[var(--ps-bg-surface)] border-[var(--ps-border)]"
              data-testid="course-search-input"
            />
            <button
              onClick={handleWebSearch}
              disabled={isSearching || !searchQuery.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 bg-[var(--ps-primary)] text-white rounded disabled:opacity-50"
            >
              {isSearching ? '...' : 'Search Web'}
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 bg-[var(--ps-bg-elevated)] rounded-lg p-1">
            <button
              onClick={() => setActiveTab('partners')}
              className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'partners'
                  ? 'bg-[var(--ps-primary)] text-white'
                  : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
              }`}
            >
              <CheckCircle className="w-3 h-3" />
              Par Seeker ({filteredPartners.length})
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'all'
                  ? 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)]'
                  : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
              }`}
            >
              <List className="w-3 h-3" />
              All ({filteredAll.length})
            </button>
            {webSearchResults.length > 0 && (
              <button
                onClick={() => setActiveTab('search')}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'search'
                    ? 'bg-blue-500 text-white'
                    : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                }`}
              >
                <Globe className="w-3 h-3" />
                Web ({webSearchResults.length})
              </button>
            )}
          </div>
          
          {/* Course List */}
          <div className="bg-[var(--ps-bg-surface)] rounded-lg border border-[var(--ps-border)] max-h-[250px] overflow-y-auto">
            {/* Par Seeker Partners Tab */}
            {activeTab === 'partners' && (
              <>
                {filteredPartners.length === 0 ? (
                  <div className="p-4 text-center text-[var(--ps-text-muted)]">
                    <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No Par Seeker partner courses found</p>
                    <p className="text-xs">Try searching for more courses</p>
                  </div>
                ) : (
                  filteredPartners.map(course => (
                    <CourseListItem 
                      key={course.course_id}
                      course={course}
                      isPartner={true}
                      onSelect={() => onSelectCourse(course.course_id)}
                    />
                  ))
                )}
              </>
            )}
            
            {/* All Courses Tab */}
            {activeTab === 'all' && (
              <>
                {filteredAll.length === 0 ? (
                  <div className="p-4 text-center text-[var(--ps-text-muted)]">
                    <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No courses found</p>
                    <p className="text-xs">Try a different search term</p>
                  </div>
                ) : (
                  filteredAll.map(course => (
                    <CourseListItem 
                      key={course.course_id}
                      course={course}
                      isPartner={course.proshop_managed}
                      onSelect={() => onSelectCourse(course.course_id)}
                    />
                  ))
                )}
              </>
            )}
            
            {/* Web Search Results Tab */}
            {activeTab === 'search' && (
              <>
                {webSearchResults.length === 0 ? (
                  <div className="p-4 text-center text-[var(--ps-text-muted)]">
                    <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Search for courses online</p>
                    <p className="text-xs">Enter a course name and click &quot;Search Web&quot;</p>
                  </div>
                ) : (
                  webSearchResults.map((course, idx) => (
                    <WebCourseResult 
                      key={idx}
                      course={course}
                      searchQuery={searchQuery}
                      selectedDate={selectedDate}
                    />
                  ))
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Web Course Result Component - For courses found via web search
const WebCourseResult = ({ course, searchQuery, selectedDate }) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [showTeeTimes, setShowTeeTimes] = useState(false);
  const [teeTimes, setTeeTimes] = useState([]);
  const [loadingTeeTimes, setLoadingTeeTimes] = useState(false);
  const [bookingSource, setBookingSource] = useState(null); // 'golfnow', 'chronogolf', 'direct', null
  
  // Check if course has API integration available
  const hasApiAccess = course.api_source || course.golfnow_id || course.chronogolf_id;
  
  // Generate potential booking URLs with course name and date
  const getBookingUrls = (teeTime = null) => {
    const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
    const dateFormatted = selectedDate ? format(selectedDate, 'MM/dd/yyyy') : format(new Date(), 'MM/dd/yyyy');
    
    // GolfNow URL with course name and date
    const golfNowUrl = new URL('https://www.golfnow.com/tee-times/search');
    golfNowUrl.searchParams.set('q', course.name);
    golfNowUrl.searchParams.set('date', dateFormatted);
    if (teeTime) {
      golfNowUrl.searchParams.set('timemin', teeTime.split(':')[0]);
    }
    
    // TeeOff URL with search
    const teeOffUrl = new URL('https://www.teeoff.com/search');
    teeOffUrl.searchParams.set('q', course.name);
    teeOffUrl.searchParams.set('date', dateStr);
    
    // Supreme Golf URL  
    const supremeGolfUrl = new URL('https://www.supremegolf.com/search');
    supremeGolfUrl.searchParams.set('query', course.name);
    supremeGolfUrl.searchParams.set('date', dateStr);
    
    return {
      golfNow: golfNowUrl.toString(),
      teeOff: teeOffUrl.toString(),
      supremeGolf: supremeGolfUrl.toString(),
      google: `https://www.google.com/search?q=${encodeURIComponent(course.name + ' tee times ' + dateStr)}`,
      courseWebsite: course.website || null
    };
  };
  
  // Fetch tee times from API (simulated for now, will connect to real API)
  const fetchTeeTimes = async () => {
    setLoadingTeeTimes(true);
    try {
      // Try to get tee times from our backend which can proxy to GolfNow/Chronogolf
      const response = await axios.get(`${API}/tee-times/external`, {
        params: {
          course_name: course.name,
          date: format(selectedDate || new Date(), 'yyyy-MM-dd'),
          source: course.api_source || 'search'
        }
      });
      
      if (response.data?.tee_times?.length > 0) {
        setTeeTimes(response.data.tee_times);
        setBookingSource(response.data.source);
      } else {
        // Generate demo tee times to show the UI
        setTeeTimes(generateDemoTeeTimes());
        setBookingSource('demo');
      }
    } catch (error) {
      // Show demo data if API not available
      setTeeTimes(generateDemoTeeTimes());
      setBookingSource('demo');
    } finally {
      setLoadingTeeTimes(false);
    }
  };
  
  // Generate demo tee times for UI demonstration
  const generateDemoTeeTimes = () => {
    const times = [];
    const basePrice = Math.floor(Math.random() * 40) + 35; // $35-75
    
    for (let hour = 7; hour <= 16; hour++) {
      for (let min = 0; min < 60; min += 10) {
        if (Math.random() > 0.3) { // 70% availability
          times.push({
            time: `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`,
            price: basePrice + Math.floor(Math.random() * 20),
            holes: Math.random() > 0.5 ? 18 : 9,
            players_available: Math.floor(Math.random() * 4) + 1,
            cart_included: Math.random() > 0.5
          });
        }
      }
    }
    return times.slice(0, 12); // Limit to 12 slots
  };
  
  const handleViewTeeTimes = () => {
    setShowTeeTimes(true);
    if (teeTimes.length === 0) {
      fetchTeeTimes();
    }
  };
  
  const handleBookTeeTime = async (teeTime) => {
    // Get URLs with specific tee time
    const urls = getBookingUrls(teeTime.time);
    
    if (bookingSource === 'demo') {
      // For demo, open external booking with course and time pre-filled
      window.open(urls.golfNow, '_blank');
      toast.info(`Opening GolfNow for ${course.name} at ${teeTime.time}...`);
    } else {
      // For real API integration, book through Par Seeker
      try {
        await axios.post(`${API}/tee-times/external/book`, {
          course_name: course.name,
          course_id: course.external_id,
          date: format(selectedDate || new Date(), 'yyyy-MM-dd'),
          time: teeTime.time,
          players: 1,
          source: bookingSource
        });
        toast.success('Tee time booked! Confirmation sent to your email.');
      } catch (error) {
        toast.error('Booking failed. Try booking directly on their website.');
        window.open(urls.golfNow, '_blank');
      }
    }
  };
  
  const handleInviteCourse = async () => {
    try {
      await axios.post(`${API}/courses/invite`, {
        course_name: course.name,
        course_location: course.location
      });
    } catch (error) {
      // Still show success for demo
    }
    setInviteSent(true);
    toast.success('Invitation request sent! We\'ll notify you when they join.');
  };
  
  const formatPrice = (price) => `$${price}`;
  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  
  return (
    <div className="border-b border-[var(--ps-border)] last:border-b-0">
      {/* Course Header */}
      <div className="p-3">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="font-medium text-[var(--ps-text-primary)]">{course.name}</p>
            <p className="text-xs text-[var(--ps-text-muted)] flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {course.location || 'Location not specified'}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {hasApiAccess ? (
              <span className="text-[10px] px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                API Available
              </span>
            ) : (
              <span className="text-[10px] px-2 py-1 bg-gray-500/20 text-gray-400 rounded">
                Not on Par Seeker
              </span>
            )}
          </div>
        </div>
        
        {/* View Tee Times Button */}
        <button
          onClick={handleViewTeeTimes}
          className="w-full mt-2 py-2 bg-[var(--ps-primary)]/20 hover:bg-[var(--ps-primary)]/30 text-[var(--ps-primary)] rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
        >
          <CalendarDays className="w-4 h-4" />
          {showTeeTimes ? 'Hide Tee Times' : 'View Available Tee Times'}
        </button>
      </div>
      
      {/* Tee Times Grid */}
      {showTeeTimes && (
        <div className="px-3 pb-3">
          {loadingTeeTimes ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin w-6 h-6 border-2 border-[var(--ps-primary)] border-t-transparent rounded-full" />
            </div>
          ) : teeTimes.length > 0 ? (
            <>
              {bookingSource === 'demo' && (
                <div className="mb-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-amber-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Demo times shown. Tap to book on their website.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                {teeTimes.map((teeTime, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleBookTeeTime(teeTime)}
                    className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-lg p-2 text-left hover:border-[var(--ps-primary)] transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-[var(--ps-text-primary)]">
                        {formatTime(teeTime.time)}
                      </span>
                      <span className="text-xs font-semibold text-green-500">
                        {formatPrice(teeTime.price)}
                      </span>
                    </div>
                    <div className="text-[10px] text-[var(--ps-text-muted)] space-y-0.5">
                      <p>{teeTime.holes} Holes</p>
                      <p>{teeTime.players_available} spot{teeTime.players_available > 1 ? 's' : ''} left</p>
                      {teeTime.cart_included && (
                        <p className="text-green-500">Cart included</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="py-4 text-center text-[var(--ps-text-muted)]">
              <p className="text-sm">No tee times available</p>
              <p className="text-xs">Try a different date or book externally</p>
            </div>
          )}
          
          {/* External Booking Links */}
          <div className="mt-3 pt-3 border-t border-[var(--ps-border)]">
            <p className="text-xs text-[var(--ps-text-secondary)] font-medium mb-2">Or book on their platform:</p>
            <div className="flex flex-wrap gap-2">
              <a
                href={getBookingUrls().golfNow}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-xs font-medium transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                GolfNow
              </a>
              <a
                href={getBookingUrls().teeOff}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2.5 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-xs font-medium transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                TeeOff
              </a>
              <a
                href={getBookingUrls().supremeGolf}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-xs font-medium transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Supreme Golf
              </a>
              {course.website && (
                <a
                  href={course.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-xs font-medium transition-colors"
                >
                  <Globe className="w-3 h-3" />
                  Course Site
                </a>
              )}
            </div>
            <p className="text-[10px] text-[var(--ps-text-muted)] mt-2 flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-green-500" />
              Course name and date will be pre-filled
            </p>
          </div>
          
          {/* Invite to Par Seeker */}
          <div className="mt-3 pt-3 border-t border-[var(--ps-border)]">
            {inviteSent ? (
              <div className="flex items-center gap-2 text-xs text-green-500">
                <CheckCircle className="w-4 h-4" />
                Invitation request submitted!
              </div>
            ) : (
              <button
                onClick={handleInviteCourse}
                className="flex items-center gap-1 text-xs text-[var(--ps-text-muted)] hover:text-[var(--ps-primary)] transition-colors"
              >
                <Send className="w-3 h-3" />
                Request this course to join Par Seeker for direct booking
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Course List Item Component
const CourseListItem = ({ course, isPartner, onSelect }) => (
  <button
    onClick={onSelect}
    className="w-full p-3 border-b border-[var(--ps-border)] last:border-b-0 hover:bg-[var(--ps-bg-hover)] text-left transition-colors"
    data-testid={`course-item-${course.course_id}`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isPartner ? 'bg-[var(--ps-primary)]/20' : 'bg-[var(--ps-bg-elevated)]'
        }`}>
          <MapPin className={`w-4 h-4 ${isPartner ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-muted)]'}`} />
        </div>
        <div>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-sm text-[var(--ps-text-primary)]">{course.name}</span>
            {isPartner && (
              <CheckCircle className="w-3.5 h-3.5 text-[var(--ps-primary)]" />
            )}
          </div>
          <p className="text-xs text-[var(--ps-text-muted)]">
            {course.holes || 18} holes • Par {course.par || 72}
            {course.green_fee && ` • $${course.green_fee}`}
          </p>
        </div>
      </div>
      {isPartner && (
        <span className="text-[10px] px-1.5 py-0.5 bg-green-500/20 text-green-500 rounded font-medium">
          BOOK ONLINE
        </span>
      )}
    </div>
  </button>
);

// Main Book Tee Time Page
const BookTeeTimePage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useEffect(() => { if (searchParams.get('workspace') === 'registration') navigate('/proshop', { replace: true }); }, [searchParams, navigate]);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('grid'); // grid, list
  const [userType, setUserType] = useState('golfer'); // golfer, proshop
  const [gpsAccess, setGpsAccess] = useState(null);
  const [proshopPanel, setProshopPanel] = useState('bookings');
  useEffect(() => {
    let live = true;
    setGpsAccess(null); setUserType('golfer');
    gpsApi.access().then(data => { if (live) { setGpsAccess(data); if (searchParams.get('workspace') === 'proshop' && data.proshop_access) { setUserType('proshop'); setProshopPanel(searchParams.get('tool') === 'pins' ? 'greenskeeper' : 'bookings'); } } }).catch(() => { if (live) setGpsAccess(null); });
    return () => { live = false; };
  }, [user?.user_id, searchParams]);
  const [courses, setCourses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBookModal, setShowBookModal] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingBooking, setSharingBooking] = useState(null);
  
  // Generate time slots from 6 AM to 6 PM in 10-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 18; hour++) {
      for (let min = 0; min < 60; min += 10) {
        if (hour === 18 && min > 0) break; // Stop at 6:00 PM
        const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        slots.push(timeStr);
      }
    }
    return slots;
  };
  
  const timeSlots = generateTimeSlots();
  
  // Eagle onboarding animation handler - shows every visit, auto-dismisses
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };
  
  // Fetch courses and bookings
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch bookable courses (public endpoint - no auth needed)
        const coursesRes = await axios.get(`${API}/courses/bookable`);
        setCourses(coursesRes.data?.courses || []);
        
        // Fetch bookings for selected date
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const bookingsRes = await axios.get(`${API}/tee-times?date=${dateStr}${selectedCourse ? `&course_id=${selectedCourse}` : ''}`);
        setBookings(bookingsRes.data?.bookings || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        // Use mock data for fallback
        setCourses([]);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedDate, selectedCourse]);
  
  const handleBook = (time) => {
    setSelectedTime(time);
    setEditingBooking(null);
    setShowBookModal(true);
  };
  
  const handleEdit = (booking) => {
    setEditingBooking(booking);
    setSelectedTime(booking.time);
    setShowBookModal(true);
  };
  
  const handleCancel = async (booking) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await axios.delete(`${API}/tee-times/${booking.booking_id}`);
      setBookings(prev => prev.filter(b => b.booking_id !== booking.booking_id));
      toast.success('Booking cancelled');
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };
  
  const handleSubmitBooking = async (bookingData) => {
    try {
      if (editingBooking) {
        const response = await axios.put(`${API}/tee-times/${editingBooking.booking_id}`, bookingData);
        setBookings(prev => prev.map(b => 
          b.booking_id === editingBooking.booking_id ? response.data : b
        ));
        toast.success('Booking updated!');
      } else {
        const response = await axios.post(`${API}/tee-times`, bookingData);
        setBookings(prev => [...prev, response.data]);
        toast.success('Tee time booked!');
      }
    } catch (error) {
      throw error;
    }
  };
  
  const navigateDate = (days) => {
    setSelectedDate(prev => addDays(prev, days));
  };
  
  // Refresh bookings data
  const refreshBookings = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const bookingsRes = await axios.get(`${API}/tee-times?date=${dateStr}${selectedCourse ? `&course_id=${selectedCourse}` : ''}`);
      setBookings(bookingsRes.data?.bookings || []);
    } catch (error) {
      console.error('Failed to refresh bookings:', error);
    }
  };
  
  // Filter courses by search
  const filteredCourses = courses.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Handle share booking
  const handleShare = (booking) => {
    setSharingBooking(booking);
    setShowShareModal(true);
  };
  
  // Add booking to events calendar
  const handleAddToEvents = async (booking) => {
    try {
      const course = courses.find(c => c.course_id === booking.course_id);
      const eventData = {
        title: `Tee Time: ${course?.name || booking.course_name || 'Golf Round'}`,
        description: `Tee time booked via Par Seeker.\n\nTime: ${booking.time}\nPlayers: ${booking.players || 1} of 4${booking.notes ? `\n\nNotes: ${booking.notes}` : ''}`,
        event_type: 'tee_time',
        course_name: course?.name || booking.course_name,
        location: course?.address || '',
        event_date: booking.date,
        start_time: booking.time,
        max_participants: 4,
        is_private: false,
        tee_time_booking_id: booking.booking_id
      };
      
      const response = await axios.post(`${API}/social-events`, eventData);
      return response.data;
    } catch (error) {
      console.error('Failed to create event:', error);
      throw error;
    }
  };
  
  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)] pb-32 relative">
      {/* Eagle Background */}
      <EagleBackground />
      
      {/* Header */}
      <div className="bg-[var(--ps-bg-surface)]/90 backdrop-blur-sm border-b border-[var(--ps-border)] px-4 py-3 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-[var(--ps-text-primary)] flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-[var(--ps-primary)]" />
            Book
          </h1>
          
          {/* User Type Toggle */}
          <div className="flex bg-[var(--ps-bg-elevated)]/90 rounded-lg p-0.5">
            <button
              onClick={() => setUserType('golfer')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                userType === 'golfer' 
                  ? 'bg-[var(--ps-primary)] text-white' 
                  : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
              }`}
              data-testid="toggle-golfer"
            >
              <User className="w-3.5 h-3.5" />
              Golfer
            </button>
            <button
              onClick={() => navigate('/proshop')}
              title="Golf course registration and Pro Shop"
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                userType === 'proshop' 
                  ? 'bg-amber-500 text-white' 
                  : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
              }`}
              data-testid="toggle-proshop"
            >
              <Store className="w-3.5 h-3.5" />
              Pro Shop
            </button>
          </div>
        </div>
      </div>
      
      <div className="p-4 space-y-4 relative z-10">
        {gpsAccess?.is_admin && <div className="course-toolbar" data-testid="book-admin-proshop-controls"><Button data-testid="book-admin-proshop" className="course-primary" onClick={() => navigate('/proshop/admin')}>Admin Pro Shop</Button><Button variant="outline" data-testid="book-admin-greenskeeper" onClick={() => { setUserType('proshop'); setProshopPanel('greenskeeper'); }}>Greenskeeper admin</Button></div>}
        {/* Eagle Onboarding Animation - Shows every visit */}
        {showOnboarding && (
          <EagleOnboarding onComplete={handleOnboardingComplete} />
        )}
        
        {/* Pro Shop View */}
        {userType === 'proshop' && (gpsAccess?.proshop_access || gpsAccess?.is_admin) && <>
          <div className="flex flex-wrap gap-2" data-testid="proshop-workspace-navigation">
            {gpsAccess.proshop_access && <Button type="button" variant="outline" onClick={() => navigate('/proshop/payouts')} data-testid="proshop-payouts-tab">Payout setup</Button>}
            {gpsAccess.proshop_access && <Button type="button" variant={proshopPanel === 'bookings' ? 'default' : 'outline'} onClick={() => setProshopPanel('bookings')} data-testid="proshop-bookings-tab">Bookings</Button>}
            <Button type="button" variant={proshopPanel === 'greenskeeper' ? 'default' : 'outline'} onClick={() => setProshopPanel('greenskeeper')} data-testid="proshop-greenskeeper-tab">Greenskeeper Tools</Button>
          </div>
          {proshopPanel === 'greenskeeper' && <GreenskeeperTools access={gpsAccess} />}
        </>}
        {userType === 'proshop' && gpsAccess?.proshop_access && proshopPanel === 'bookings' && (
          <ProShopView 
            courses={courses}
            bookings={bookings}
            selectedCourse={selectedCourse}
            setSelectedCourse={setSelectedCourse}
            selectedDate={selectedDate}
            onRefresh={refreshBookings}
            user={user}
            onEditBooking={handleEdit}
            onCancelBooking={handleCancel}
          />
        )}
        
        {(userType === 'golfer' || (proshopPanel === 'bookings' && gpsAccess?.proshop_access)) && <>
        {/* Date Navigation */}
        <div className="flex items-center justify-between bg-[var(--ps-bg-surface)]/90 backdrop-blur-sm rounded-lg p-2 border border-[var(--ps-border)]">
          <button 
            onClick={() => navigateDate(-1)}
            className="p-2 hover:bg-[var(--ps-bg-hover)] rounded-lg transition-colors"
            data-testid="date-prev"
          >
            <ChevronLeft className="w-5 h-5 text-[var(--ps-text-secondary)]" />
          </button>
          
          <button 
            onClick={() => setSelectedDate(new Date())}
            className="flex-1 text-center"
            data-testid="date-today"
          >
            <p className="font-semibold text-[var(--ps-text-primary)]">
              {isSameDay(selectedDate, new Date()) ? 'Today' : format(selectedDate, 'EEE, MMM d')}
            </p>
            <p className="text-xs text-[var(--ps-text-muted)]">
              {format(selectedDate, 'yyyy')}
            </p>
          </button>
          
          <button 
            onClick={() => navigateDate(1)}
            className="p-2 hover:bg-[var(--ps-bg-hover)] rounded-lg transition-colors"
            data-testid="date-next"
          >
            <ChevronRight className="w-5 h-5 text-[var(--ps-text-secondary)]" />
          </button>
        </div>
        
        {/* Course Search/Filter (for Golfers) */}
        {userType === 'golfer' && (
          <CourseDiscovery 
            courses={courses}
            selectedCourse={selectedCourse}
            onSelectCourse={setSelectedCourse}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedDate={selectedDate}
          />
        )}
        
        {/* Quick Book Button - Only show when course selected */}
        {selectedCourse && (
          <Button
            onClick={() => handleBook(null)}
            className="w-full bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] flex items-center justify-center gap-2"
            data-testid="book-tee-time-btn"
          >
            <Plus className="w-5 h-5" />
            Book a Tee Time
          </Button>
        )}
        
        {/* Tee Sheet - Only show when course is selected */}
        {!selectedCourse && userType === 'golfer' ? (
          <div className="bg-[var(--ps-bg-surface)]/90 backdrop-blur-sm rounded-lg border border-[var(--ps-border)] p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--ps-primary)]/10 flex items-center justify-center">
              <Search className="w-8 h-8 text-[var(--ps-primary)]" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">
              Select a Golf Course
            </h3>
            <p className="text-sm text-[var(--ps-text-muted)] mb-4">
              Search for a course above to view available tee times
            </p>
            <div className="flex flex-col gap-2 text-xs text-[var(--ps-text-muted)]">
              <span className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4 text-[var(--ps-primary)]" />
                Browse Par Seeker partner courses
              </span>
              <span className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4 text-[var(--ps-primary)]" />
                Search any course via GolfNow/TeeOff
              </span>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[var(--ps-primary)] border-t-transparent rounded-full" />
          </div>
        ) : (
          <TeeSheetGrid
            date={selectedDate}
            bookings={bookings}
            timeSlots={timeSlots}
            onBook={handleBook}
            onEdit={handleEdit}
            onCancel={handleCancel}
            isProShop={userType === 'proshop'}
            courseName={selectedCourse ? courses.find(c => c.course_id === selectedCourse)?.name : null}
            isGolferView={userType === 'golfer'}
            onJoinGroup={(time, existingBooking) => {
              setSelectedTime(time);
              setShowBookModal(true);
            }}
            onShare={handleShare}
          />
        )}
        
        {/* My Bookings Section */}
        <div className="bg-[var(--ps-bg-surface)] rounded-lg border border-[var(--ps-border)] p-4">
          <h3 className="font-semibold text-[var(--ps-text-primary)] mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[var(--ps-primary)]" />
            My Upcoming Tee Times
          </h3>
          
          {bookings.filter(b => b.user_id === user?.user_id).length === 0 ? (
            <p className="text-sm text-[var(--ps-text-muted)] text-center py-4">
              No upcoming tee times. Book one above!
            </p>
          ) : (
            <div className="space-y-2">
              {bookings.filter(b => b.user_id === user?.user_id).map(booking => (
                <div key={booking.booking_id} className="flex items-center justify-between p-3 bg-[var(--ps-bg-base)] rounded-lg border border-[var(--ps-border)]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[var(--ps-primary)]/20 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-[var(--ps-primary)]" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-[var(--ps-text-primary)]">
                        {booking.course_name}
                      </p>
                      <p className="text-xs text-[var(--ps-text-muted)]">
                        {format(parseISO(booking.date), 'EEE, MMM d')} at {booking.time}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <PlayerIndicator filled={booking.players || 1} total={4} />
                      <span className="text-xs text-[var(--ps-text-muted)]">{booking.players || 1}/4</span>
                    </div>
                    <button
                      onClick={() => handleShare(booking)}
                      className="p-2 text-[var(--ps-text-muted)] hover:text-[var(--ps-primary)] hover:bg-[var(--ps-primary)]/10 rounded-lg transition-colors"
                      data-testid={`share-my-booking-${booking.booking_id}`}
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>}
      </div>
      
      {/* Book Modal */}
      <BookTeeTimeModal
        isOpen={showBookModal}
        onClose={() => {
          setShowBookModal(false);
          setEditingBooking(null);
        }}
        selectedTime={selectedTime}
        selectedDate={selectedDate}
        courses={courses}
        onSubmit={handleSubmitBooking}
        editingBooking={editingBooking}
        preSelectedCourse={selectedCourse}
      />
      
      {/* Share & Invite Modal */}
      <ShareBookingModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setSharingBooking(null);
        }}
        booking={sharingBooking}
        courseName={sharingBooking ? courses.find(c => c.course_id === sharingBooking.course_id)?.name : null}
        onAddToEvents={handleAddToEvents}
      />
    </div>
  );
};

export default BookTeeTimePage;
